/**
 * Stats API — aggregates over send_log, plus share card.
 */

import { useQuery } from "@tanstack/react-query";

import { api, type ApiError } from "./client";

// ============================================================
// Types
// ============================================================

export interface StatsOverviewBucket {
  ts: string;
  sent: number;
  failed: number;
}

export interface StatsOverview {
  days: number;
  sent: number;
  failed: number;
  skipped: number;
  unique_chats: number;
  unique_accounts: number;
  delta_sent_pct: number;
  series: StatsOverviewBucket[];
  lifetime_sent: number;
}

export interface TimeseriesPoint {
  ts: string;
  sent: number;
  failed: number;
}

export interface TimeseriesResponse {
  bucket: "day" | "hour";
  days: number;
  points: TimeseriesPoint[];
}

export interface TopChatItem {
  chat_id: number | null;
  chat_title: string | null;
  sent: number;
  failed: number;
}

export interface TopAccountItem {
  account_id: number | null;
  name: string | null;
  phone: string | null;
  sent: number;
  failed: number;
}

export interface TopChatsResponse {
  days: number;
  items: TopChatItem[];
}

export interface TopAccountsResponse {
  days: number;
  items: TopAccountItem[];
}

export interface ShareCard {
  title: string;
  lines: string[];
  footer: string;
  share_url: string;
  deep_link: string;
}

// ============================================================
// Queries
// ============================================================

export function useStatsOverview(days: number) {
  return useQuery<StatsOverview, ApiError>({
    queryKey: ["stats", "overview", days],
    queryFn: () => api.get<StatsOverview>(`/stats/overview?days=${days}`),
    staleTime: 30_000,
  });
}

export function useStatsTimeseries(params: { days: number; bucket?: "day" | "hour" }) {
  const bucket = params.bucket ?? "day";
  return useQuery<TimeseriesResponse, ApiError>({
    queryKey: ["stats", "timeseries", params.days, bucket],
    queryFn: () =>
      api.get<TimeseriesResponse>(`/stats/timeseries?days=${params.days}&bucket=${bucket}`),
    staleTime: 30_000,
  });
}

export function useStatsTopChats(params: { days: number; limit?: number }) {
  const limit = params.limit ?? 10;
  return useQuery<TopChatsResponse, ApiError>({
    queryKey: ["stats", "top-chats", params.days, limit],
    queryFn: () =>
      api.get<TopChatsResponse>(`/stats/top-chats?days=${params.days}&limit=${limit}`),
    staleTime: 30_000,
  });
}

export function useStatsTopAccounts(params: { days: number; limit?: number }) {
  const limit = params.limit ?? 10;
  return useQuery<TopAccountsResponse, ApiError>({
    queryKey: ["stats", "top-accounts", params.days, limit],
    queryFn: () =>
      api.get<TopAccountsResponse>(`/stats/top-accounts?days=${params.days}&limit=${limit}`),
    staleTime: 30_000,
  });
}

export function useShareCard(days: number, opts?: { enabled?: boolean }) {
  return useQuery<ShareCard, ApiError>({
    queryKey: ["stats", "share-card", days],
    queryFn: () => api.get<ShareCard>(`/stats/share-card?days=${days}`),
    enabled: opts?.enabled ?? true,
    staleTime: 60_000,
  });
}
